import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import CourseCard from './CourseCard';
import CourseFilter from './CourseFilter';

const CoursesSection = styled.section`
  padding: 80px 0;
  background-color: #0A1535;
  color: white;
  
  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 2rem;
  color: white;
  text-shadow: 0 0 15px rgba(0, 229, 255, 0.3);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, transparent, #00E5FF, transparent);
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  padding: 0 15px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const NoResults = styled(motion.div)`
  text-align: center;
  padding: 40px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  grid-column: 1 / -1;
  
  @media (max-width: 576px) {
    padding: 20px;
  }
`;

const Courses: React.FC = () => {
  const [filteredCourses, setFilteredCourses] = useState<any[]>([]);
  
  const courses = [
    {
      id: 1,
      title: 'MBA em Enfermagem em Cardiologia e Hemodinâmica',
      icon: '',
      link: 'https://ogdjlwis.manus.space/',
      category: 'enfermagem'
    },
    {
      id: 2,
      title: 'Pós-graduação em MBA em Enfermagem em Emergência',
      icon: '',
      link: 'https://bnateqtp.manus.space/',
      category: 'enfermagem'
    },
    {
      id: 3,
      title: 'MBA em UTI Adulto com Ênfase em Cardiologia',
      icon: '',
      link: 'https://dazxpkpl.manus.space/',
      category: 'uti'
    },
    {
      id: 4,
      title: 'MBA em Enfermagem Dermatológica com Ênfase em Feridas',
      icon: '',
      link: 'https://troxfrsw.manus.space/',
      category: 'especializacao'
    },
    {
      id: 5,
      title: 'MBA em Centro Cirúrgico, Sala de Recuperação Anestésica e CME',
      icon: '',
      link: 'https://xugmcuwy.manus.space/',
      category: 'especializacao'
    },
    {
      id: 6,
      title: 'MBA em Enfermagem em Estomaterapia',
      icon: '',
      link: 'https://anovjuag.manus.space/',
      category: 'especializacao'
    },
    {
      id: 7,
      title: 'MBA em UTI Neonatal e Pediátrica',
      icon: '',
      link: 'https://insngnov.manus.space/',
      category: 'uti'
    },
    {
      id: 8,
      title: 'MBA em Enfermagem em Oncologia',
      icon: '',
      link: 'https://jstmgylk.manus.space/',
      category: 'enfermagem'
    },
    {
      id: 9,
      title: 'MBA em Enfermagem do Trabalho',
      icon: '',
      link: 'https://jlaxsfjk.manus.space/',
      category: 'enfermagem'
    },
    {
      id: 10,
      title: 'MBA em Gestão em Saúde',
      icon: '',
      link: 'https://dqflyiqv.manus.space/',
      category: 'gestao'
    },
    {
      id: 11,
      title: 'MBA em Cuidados Paliativos',
      icon: '',
      link: 'https://ncimvdkv.manus.space/',
      category: 'especializacao'
    },
    {
      id: 12,
      title: 'MBA em Auditoria em Saúde',
      icon: '',
      link: 'https://ktzijyfz.manus.space/',
      category: 'gestao'
    }
  ];

  useEffect(() => {
    setFilteredCourses(courses);
  }, []);

  const filterCourses = (_filter: string, search: string = '') => {
    let result = [...courses];
    
    // Aplicar filtro por termo de busca
    if (search.trim() !== '') {
      const searchLower = search.toLowerCase();
      result = result.filter(course => 
        course.title.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredCourses(result);
  };

  return (
    <CoursesSection id="cursos">
      <div className="container">
        <SectionTitle>Nossos Cursos de Pós-Graduação</SectionTitle>
        
        <CourseFilter onFilterChange={filterCourses} />
        
        <AnimatePresence>
          <CoursesGrid>
            {filteredCourses.length > 0 ? (
              filteredCourses.map((course) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4 }}
                  layout
                >
                  <CourseCard 
                    title={course.title}
                    icon={course.icon}
                    link={course.link}
                  />
                </motion.div>
              ))
            ) : (
              <NoResults
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <h3>Nenhum curso encontrado</h3>
                <p>Tente ajustar os termos de busca.</p>
              </NoResults>
            )}
          </CoursesGrid>
        </AnimatePresence>
      </div>
    </CoursesSection>
  );
};

export default Courses;
